import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert, Box, Button, Chip, Dialog, DialogActions, DialogContent, DialogTitle, Grid, Paper, Stack, Table, TableBody,
  TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from '@mui/material';
import { api, ApiError } from '../api/client';
import type { ContractsConfig, IssueRequest, Subscription, TxRequestResult } from '../api/types';
import { AddressWithCopy, PageHeader, fmtUnits, shortAddr, TxHashLink } from '../components/common';
import { IssueStatusChip } from './Assets';
import { useAuth } from '../auth/AuthContext';
import { TxProgressDialog, TxResult } from '../components/TxProgressDialog';

/** 발행자(ST) — 청약 공고 등록 + 목록 */
export function IssuerSubscriptions() {
  const qc = useQueryClient();
  const nav = useNavigate();
  // 공고 등록은 발행자(ST) 업무다 — 관리자는 목록·배포만(1차 확인 20번). API 도 issuer_st 로 제한한다.
  const { hasRole } = useAuth();
  const canCreate = hasRole('issuer_st');
  const list = useQuery({ queryKey: ['subscriptions'], queryFn: () => api.get<Subscription[]>('/subscriptions') });
  const [f, setF] = useState({ tokenName: '', symbol: '', totalAmount: '', unitPrice: '', raiseTarget: '' });
  const [msg, setMsg] = useState<{ sev: 'success' | 'error'; text: string } | null>(null);
  const set = (k: keyof typeof f) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });

  const create = useMutation({
    mutationFn: () => {
      const now = new Date();
      const end = new Date(now.getTime() + 7 * 864e5);
      return api.post('/subscriptions', {
        ...f, periodStart: now.toISOString(), periodEnd: end.toISOString(),
      });
    },
    onSuccess: () => { setMsg({ sev: 'success', text: '공고가 등록되었습니다.' }); void qc.invalidateQueries({ queryKey: ['subscriptions'] }); },
    onError: (e) => setMsg({ sev: 'error', text: e instanceof ApiError ? e.message : '등록 실패' }),
  });

  return (
    <Box>
      <PageHeader crumb="발행자(ST) · 청약" title="청약 공고 관리" desc="공고를 등록하고 청약자에게 발행·분배합니다." />
      {msg && <Alert severity={msg.sev} sx={{ mb: 2 }} onClose={() => setMsg(null)}>{msg.text}</Alert>}
      <Grid container spacing={3}>
        {canCreate && (
        <Grid item xs={12} md={5}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h2" mb={2}>공고 등록</Typography>
            <Stack spacing={2}>
              <TextField label="토큰명" value={f.tokenName} onChange={set('tokenName')} />
              <TextField label="심볼" value={f.symbol} onChange={set('symbol')} />
              <TextField label="총 수량" value={f.totalAmount} onChange={set('totalAmount')} type="number" />
              <TextField label="단가(KRWS)" value={f.unitPrice} onChange={set('unitPrice')} type="number" />
              <TextField label="모집 목표(KRWS)" value={f.raiseTarget} onChange={set('raiseTarget')} type="number" />
              <Button variant="contained" onClick={() => create.mutate()} disabled={create.isPending || !f.tokenName || !f.symbol}>
                공고 등록
              </Button>
            </Stack>
          </Paper>
        </Grid>
        )}
        <Grid item xs={12} md={canCreate ? 7 : 12}>
          <Paper>
            <Table size="small">
              <TableHead><TableRow><TableCell>토큰</TableCell><TableCell align="right">단가</TableCell><TableCell align="right">총량</TableCell><TableCell>상태</TableCell><TableCell align="right">운영</TableCell></TableRow></TableHead>
              <TableBody>
                {(list.data ?? []).map((s) => (
                  <TableRow key={s.id} hover><TableCell><b>{s.tokenName}</b> {s.symbol}</TableCell>
                    <TableCell align="right">{fmtUnits(s.unitPrice, 0)}</TableCell>
                    <TableCell align="right">{fmtUnits(s.totalAmount, 0)}</TableCell>
                    <TableCell>{s.status}</TableCell>
                    <TableCell align="right">
                      {/* 마감 → 컨트랙트 배포 → 발행 → 정산 (SFR-003) */}
                      <Button size="small" variant="outlined" onClick={() => nav(`/issuer/subscriptions/${s.id}`)}>운영</Button>
                    </TableCell></TableRow>
                ))}
                {list.data?.length === 0 && <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3, color: 'text.secondary' }}>공고가 없습니다.</TableCell></TableRow>}
              </TableBody>
            </Table>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
}

/**
 * 발행자(SC) — 스테이블코인 관리 (SFR-005).
 * 발행 요청 심사·발행(mint) · 보유 현황 조회 · 소각(burnFrom) · 지갑 동결/해제.
 */
export function IssuerStablecoin() {
  const cfg = useQuery({ queryKey: ['contracts'], queryFn: () => api.get<ContractsConfig>('/config/contracts') });
  const sc = cfg.data?.stablecoin ?? null;
  const [txOpen, setTxOpen] = useState(false);
  const [txTitle, setTxTitle] = useState('스테이블코인 발행');
  const [pending, setPending] = useState(false);
  const [result, setResult] = useState<TxResult | null>(null);
  const [error, setError] = useState<string | null>(null);

  /** 온체인 액션 공통 실행기 — 진행 모달 + revert 사유 표시 */
  const run = (title: string, fn: () => Promise<TxRequestResult>) => async () => {
    setTxTitle(title); setTxOpen(true); setPending(true); setResult(null); setError(null);
    try {
      const r = await fn();
      setResult({ status: r.status, txHash: r.txHash, revertReason: r.revertReason });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : `${title} 실패`);
    } finally {
      setPending(false);
    }
  };

  return (
    <Box>
      <PageHeader crumb="발행자(SC) · 관리" title="스테이블코인 관리"
        desc="발행 요청 심사·발행, 보유 현황 조회, 소각·동결을 수행합니다. 되돌릴 수 없는 조치는 신중히 실행하세요." />
      {sc && (
        <Paper sx={{ p: 2, mb: 3 }}>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color="text.secondary">KRWS 컨트랙트</Typography>
            <AddressWithCopy address={sc} />
          </Stack>
        </Paper>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12}><IssueQueueCard sc={sc} run={run} /></Grid>
        <Grid item xs={12} md={6}><IssueCard sc={sc} run={run} /></Grid>
        <Grid item xs={12} md={6}><BalanceCard sc={sc} /></Grid>
        <Grid item xs={12} md={6}><BurnCard sc={sc} run={run} /></Grid>
        <Grid item xs={12} md={6}><FreezeCard sc={sc} run={run} /></Grid>
        <Grid item xs={12}><BatchCard sc={sc} run={run} /></Grid>
      </Grid>

      <TxProgressDialog open={txOpen} title={txTitle} pending={pending} result={result} error={error}
        onClose={() => setTxOpen(false)} />
    </Box>
  );
}

type ScRunner = (title: string, fn: () => Promise<TxRequestResult>) => () => void;

/**
 * 발행 요청 심사 큐 (SFR-005, A-6).
 * RFP 흐름은 "사용자 요청 → 발행자가 Registry 등록 확인 후 발행"이다.
 * 그래서 각 행에 **온체인 등록 여부**를 배지로 붙이고, 미등록이면 승인 버튼을 막는다.
 */
function IssueQueueCard({ sc, run }: { sc: string | null; run: ScRunner }) {
  const qc = useQueryClient();
  const [showAll, setShowAll] = useState(false);
  const [reject, setReject] = useState<IssueRequest | null>(null);
  const [reason, setReason] = useState('');
  const [err, setErr] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['issue-queue', sc, showAll],
    queryFn: () => api.get<IssueRequest[]>(`/stablecoin/${sc}/issue-requests?status=${showAll ? 'all' : 'pending'}`),
    enabled: !!sc,
  });

  const doReject = useMutation({
    mutationFn: () => api.post(`/stablecoin/${sc}/issue-requests/${reject!.id}/reject`, { reason }),
    onSuccess: () => { setReject(null); setReason(''); setErr(null); void qc.invalidateQueries({ queryKey: ['issue-queue'] }); },
    onError: (e) => setErr(e instanceof ApiError ? e.message : '반려 실패'),
  });

  const rows = list.data ?? [];

  return (
    <Paper sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
        <Box>
          <Typography variant="h2">발행 요청 심사</Typography>
          <Typography variant="body2" color="text.secondary">
            사용자 요청을 검토하고 온체인 등록(isVerified)을 확인한 뒤 발행합니다.
          </Typography>
        </Box>
        <Button size="small" onClick={() => setShowAll((v) => !v)}>
          {showAll ? '대기 건만 보기' : '전체 이력 보기'}
        </Button>
      </Stack>

      {rows.length === 0 && !list.isLoading && (
        <Typography variant="body2" color="text.secondary">
          {showAll ? '요청 이력이 없습니다.' : '심사 대기 중인 요청이 없습니다.'}
        </Typography>
      )}
      {rows.length > 0 && (
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>신청자</TableCell><TableCell align="right">수량</TableCell>
              <TableCell>수령 지갑</TableCell><TableCell>온체인 등록</TableCell>
              <TableCell>사유 · 상태</TableCell><TableCell align="right">심사</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {rows.map((r) => (
              <TableRow key={r.id} hover>
                <TableCell>
                  <b>{r.requesterLoginId ?? '—'}</b>
                  <Typography variant="caption" display="block" color="text.secondary">
                    {new Date(r.createdAt).toLocaleString('ko-KR')}
                  </Typography>
                </TableCell>
                <TableCell align="right">{r.amount} KRWS</TableCell>
                <TableCell sx={{ fontFamily: 'monospace' }}>{shortAddr(r.toWallet)}</TableCell>
                <TableCell><RegistryChip verified={r.verified ?? null} /></TableCell>
                <TableCell>
                  {r.status === 'PENDING' ? (r.memo || '—') : <IssueStatusChip status={r.status} />}
                  {r.rejectReason && (
                    <Typography variant="caption" display="block" color="text.secondary">{r.rejectReason}</Typography>
                  )}
                  {r.txHash && <TxHashLink hash={r.txHash} />}
                </TableCell>
                <TableCell align="right">
                  {r.status === 'PENDING' && (
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Tooltip title={r.verified === false ? 'Registry 미등록 지갑 — 발행이 revert 됩니다' : ''}>
                        <span>
                          <Button
                            size="small"
                            variant="contained"
                            disabled={r.verified === false}
                            onClick={run('발행 요청 승인', async () => {
                              const res = await api.post<TxRequestResult>(`/stablecoin/${sc}/issue-requests/${r.id}/approve`, {});
                              void qc.invalidateQueries({ queryKey: ['issue-queue'] });
                              return res;
                            })}
                          >
                            승인·발행
                          </Button>
                        </span>
                      </Tooltip>
                      <Button size="small" color="error" onClick={() => { setErr(null); setReject(r); }}>반려</Button>
                    </Stack>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      )}

      <Dialog open={!!reject} onClose={() => setReject(null)} maxWidth="xs" fullWidth>
        <DialogTitle>발행 요청 반려</DialogTitle>
        <DialogContent>
          <Stack spacing={2} mt={1}>
            {err && <Alert severity="error">{err}</Alert>}
            <Typography variant="body2" color="text.secondary">
              {reject?.requesterLoginId} · {reject?.amount} KRWS
            </Typography>
            <TextField label="반려 사유" value={reason} onChange={(e) => setReason(e.target.value)} multiline rows={2} />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setReject(null)}>취소</Button>
          <Button color="error" variant="contained" onClick={() => doReject.mutate()} disabled={!reason}>반려</Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
}

/** Registry 등록 배지 — null 은 "미등록"이 아니라 "체인 조회 실패"다. 구분해서 표시한다. */
function RegistryChip({ verified }: { verified: boolean | null }) {
  if (verified === null) return <Chip size="small" label="확인 불가" sx={{ fontWeight: 700 }} />;
  return verified
    ? <Chip size="small" color="success" label="✓ 등록됨" sx={{ fontWeight: 700 }} />
    : <Chip size="small" color="error" label="미등록" sx={{ fontWeight: 700 }} />;
}

/** 발행 요청 생성 → 승인(mint) */
function IssueCard({ sc, run }: { sc: string | null; run: ScRunner }) {
  const [amount, setAmount] = useState('');
  const [to, setTo] = useState('');
  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h2" mb={0.5}>직접 발행</Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        사용자 요청 없이 발행자가 임의 지갑으로 바로 발행합니다(초기 유동성 공급 등). 정식 경로는 위 심사 큐입니다.
      </Typography>
      <Stack spacing={2}>
        <TextField label="발행 수량(KRWS)" value={amount} onChange={(e) => setAmount(e.target.value)} type="number" size="small" />
        <TextField label="수신 지갑" value={to} onChange={(e) => setTo(e.target.value)} placeholder="0x…" size="small" />
        <Typography variant="caption" color="text.secondary">수신 지갑은 온체인 isVerified 상태여야 발행이 성공합니다.</Typography>
        <Button variant="contained" disabled={!sc || !amount || !to}
          onClick={run('스테이블코인 발행', async () => {
            const req = await api.post<{ id: string }>(`/stablecoin/${sc}/issue-requests`, { amount });
            return api.post<TxRequestResult>(`/stablecoin/${sc}/issue-requests/${req.id}/approve`, { to });
          })}>
          바로 발행 (mint)
        </Button>
      </Stack>
    </Paper>
  );
}

/** 보유 현황 조회 */
/** 보유 현황 조회 — 잔액과 함께 **동결 여부**를 보여준다(동결되면 잔액이 있어도 송·수신이 막힌다) */
function BalanceCard({ sc }: { sc: string | null }) {
  const [wallet, setWallet] = useState('');
  const [info, setInfo] = useState<{ balance: string; frozen: boolean; transferable: string } | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const valid = /^0x[0-9a-fA-F]{40}$/.test(wallet);

  const lookup = async () => {
    setErr(null); setInfo(null);
    try {
      const r = await api.get<{ wallet: string; balance: string; frozen: boolean; transferable: string }>(
        `/stablecoin/${sc}/freeze-status/${wallet}`,
      );
      setInfo(r);
    } catch (e) {
      setErr(e instanceof ApiError ? e.message : '조회 실패');
    }
  };

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h2" mb={2}>보유 현황 · 동결 조회</Typography>
      <Stack spacing={2}>
        <TextField label="지갑 주소" value={wallet} onChange={(e) => setWallet(e.target.value)} placeholder="0x…" size="small"
          error={!!wallet && !valid} helperText={wallet && !valid ? '유효한 주소가 아닙니다' : ' '} />
        <Button variant="outlined" disabled={!sc || !valid} onClick={() => void lookup()}>조회</Button>
        {info && (
          <Alert severity={info.frozen ? 'warning' : 'info'}>
            <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
              <span>보유 <b>{fmtUnits(info.balance)} KRWS</b></span>
              {info.frozen
                ? <Chip size="small" color="error" label="동결됨 · 송·수신 차단" sx={{ fontWeight: 700 }} />
                : <Chip size="small" color="success" label="정상" sx={{ fontWeight: 700 }} />}
            </Stack>
          </Alert>
        )}
        {err && <Alert severity="error">{err}</Alert>}
      </Stack>
    </Paper>
  );
}

/** 소각 — agent 강제소각(burnFrom) */
function BurnCard({ sc, run }: { sc: string | null; run: ScRunner }) {
  const [wallet, setWallet] = useState('');
  const [amount, setAmount] = useState('');
  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h2" mb={2}>소각</Typography>
      <Stack spacing={2}>
        <TextField label="소각 대상 지갑" value={wallet} onChange={(e) => setWallet(e.target.value)} placeholder="0x…" size="small" />
        <TextField label="소각 수량(KRWS)" value={amount} onChange={(e) => setAmount(e.target.value)} type="number" size="small" />
        <Button color="error" variant="contained" disabled={!sc || !wallet || !amount}
          onClick={() => {
            if (confirm(`${wallet} 의 ${amount} KRWS 를 소각합니다 — 되돌릴 수 없습니다. 진행할까요?`)) {
              run('스테이블코인 소각', () => api.post<TxRequestResult>(`/stablecoin/${sc}/burn`, { wallet, amount }))();
            }
          }}>
          소각 (burnFrom)
        </Button>
        <Typography variant="caption" color="text.secondary">
          규제형 스테이블코인의 관리자 강제소각(회수·상환·컴플라이언스)입니다.
        </Typography>
      </Stack>
    </Paper>
  );
}

/** 지갑 동결 / 해제 */
function FreezeCard({ sc, run }: { sc: string | null; run: ScRunner }) {
  const [wallet, setWallet] = useState('');
  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h2" mb={2}>지갑 동결 · 해제</Typography>
      <Stack spacing={2}>
        <TextField label="대상 지갑" value={wallet} onChange={(e) => setWallet(e.target.value)} placeholder="0x…" size="small" />
        <Stack direction="row" spacing={1}>
          <Button color="warning" variant="contained" fullWidth disabled={!sc || !wallet}
            onClick={run('지갑 동결', () => api.post<TxRequestResult>(`/stablecoin/${sc}/freeze`, { wallet, frozen: true }))}>
            동결
          </Button>
          <Button variant="outlined" fullWidth disabled={!sc || !wallet}
            onClick={run('동결 해제', () => api.post<TxRequestResult>(`/stablecoin/${sc}/freeze`, { wallet, frozen: false }))}>
            해제
          </Button>
        </Stack>
        <Typography variant="caption" color="text.secondary">
          동결된 지갑은 스테이블코인 송·수신이 모두 차단됩니다(WalletFrozen).
        </Typography>
      </Stack>
    </Paper>
  );
}

/**
 * 일괄 분배 (SFR-005) — 다수 지갑에 운영(agent) 보유분을 한 번에 전송.
 * 입력은 한 줄에 `주소,수량` 형식으로 받는다.
 */
function BatchCard({ sc, run }: { sc: string | null; run: ScRunner }) {
  const [text, setText] = useState('');
  const items = parseBatch(text);
  const total = items.reduce((a, i) => a + Number(i.amount || 0), 0);

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h2" mb={2}>일괄 분배</Typography>
      <Stack spacing={2}>
        <TextField
          label="분배 목록 (한 줄에 `주소,수량`)" value={text} onChange={(e) => setText(e.target.value)}
          multiline minRows={4} size="small"
          placeholder={'0xabc...,100\n0xdef...,250'}
          error={!!text.trim() && items.length === 0}
          helperText={
            text.trim() && items.length === 0
              ? '형식 오류 — `0x주소,수량` 형태로 한 줄에 하나씩 입력하세요'
              : `${items.length}건 · 합계 ${total.toLocaleString()} KRWS`
          }
        />
        <Button variant="contained" disabled={!sc || items.length === 0}
          onClick={() => {
            if (confirm(`${items.length}건 · 합계 ${total.toLocaleString()} KRWS 를 분배합니다. 진행할까요?`)) {
              run('스테이블코인 일괄 분배', () => api.post<TxRequestResult>(`/stablecoin/${sc}/batch-transfer`, { items }))();
            }
          }}>
          일괄 분배 (batchTransfer)
        </Button>
        <Typography variant="caption" color="text.secondary">
          운영(agent) 지갑 보유분에서 차감됩니다. 잔액이 부족하면 전송 전에 차단되며,
          수신자는 모두 온체인 isVerified 상태여야 합니다.
        </Typography>
      </Stack>
    </Paper>
  );
}

/** `주소,수량` 줄 목록 파싱 — 유효한 줄만 반환 */
export function parseBatch(text: string): { to: string; amount: string }[] {
  return text
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean)
    .map((l) => l.split(/[,\s]+/))
    .filter((p) => p.length >= 2 && /^0x[0-9a-fA-F]{40}$/.test(p[0]) && /^\d+(\.\d+)?$/.test(p[1]) && Number(p[1]) > 0)
    .map((p) => ({ to: p[0], amount: p[1] }));
}
